/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2019 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import Decorators from '../Decorators';
import IncrementObservable from '../Observable/IncrementObservable';
import IObservable from '../Observable/IObservable';
import Observable from '../Observable/Observable';
import TypeStrings from '../TypeStrings';
import ILoadingCriteria from './ILoadingCriteria';
import ILoadingInfo from './ILoadingInfo';

export default class LoadingInfo implements ILoadingInfo
{
    private _index: number;
    public get Index(): number { return this._index; }

    private _completeObservable: Observable<number> = new Observable(0);
    public get Complete(): number { return this._completeObservable.Value; }
    public set Complete(value: number) { this._completeObservable.Value = value; }

    private _totalObservable: Observable<number> = new Observable(0);
    public get Total(): number { return this._totalObservable.Value; }
    public set Total(value: number) { this._totalObservable.Value = value; }

    private _weightObservable: Observable<number> = new Observable(1);
    public get Weight(): number { return this._weightObservable.Value; }

    private _progressObservable: IObservable<number> = new IncrementObservable(0);
    public get Progress(): number { return this._progressObservable.Value; }
    public SetProgress(value: number): this
    {
        if (value > 100) { value = 100; }
        if (value < 0) { value = 0; }

        const complete: number = Math.floor((value / 100) * this.Total);
        this.Complete = complete > 0 ? complete : 0;
        return this;
    }

    public OnProgressChanged(fnOnProgressChanged: (progress: number) => any): this
    {
        this._progressObservable.OnChanged(e => fnOnProgressChanged(e.detail));
        return this;
    }

    private _loadingsInfos: ILoadingInfo[] = [];

    constructor(initial: number = 1, criteria: ILoadingCriteria = null)
    {
        this._totalObservable.Value = initial;

        if (criteria)
        {
            this._weightObservable.Value = criteria.Weight || 1;
            this._progressObservable = criteria.IncrementOnly === false
                ? new Observable(0)
                : new IncrementObservable(0);
        }
        else
        {
            this._weightObservable.Value = 1;
            this._progressObservable = new IncrementObservable(0);
        }

        Decorators.PerformBinding(this);

        this.configureListeners();
        this.updateProgress();
    }

    public Loaded(amount: number = 1): this
    {
        if (this._completeObservable.Value + amount > this._totalObservable.Value)
        {
            this._completeObservable.Value = this._totalObservable.Value;
        }
        else
        {
            this._completeObservable.Value += amount;
        }
        return this;
    }

    public LoadAll(): this
    {
        this._loadingsInfos.forEach(info => info.LoadAll());
        return this.SetProgress(100);
    }

    public AddTotal(amount: number = 1): this
    {
        this._totalObservable.Value += amount;
        return this;
    }

    public AddLoadingGroup(amount: number | ILoadingInfo = 1, criteria: ILoadingCriteria = null): ILoadingInfo
    {
        const loadingInfo: ILoadingInfo = typeof amount === TypeStrings.number
            ? new LoadingInfo(amount as number, criteria)
            : amount as ILoadingInfo;
        this.addLoadingInfo(loadingInfo);
        return loadingInfo;
    }

    private addLoadingInfo(info: ILoadingInfo): this
    {
        this._loadingsInfos.push(info.OnProgressChanged(this.updateProgress));
        return this.updateProgress();
    }

    private configureListeners(): void
    {
        this._completeObservable.OnChanged(this.updateProgress);
        this._totalObservable.OnChanged(this.updateProgress);
    }

    @Decorators.BindThis()
    private updateProgress(): this
    {
        let weight: number = this._weightObservable.Value;
        let complete: number = this._completeObservable.Value * weight;
        let total: number = this._totalObservable.Value * weight;

        this._loadingsInfos.forEach(loadingInfo =>
            {
                weight = loadingInfo.Weight;
                complete += loadingInfo.Complete * weight;
                total += loadingInfo.Total * weight;
            });

        this._progressObservable.Value = total ? ((complete / total) * 100) : 0;
        return this;
    }
}
